import Service, { service } from '@ember/service'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type Student from 'teacher-ui-client/models/student'
import { task } from 'ember-concurrency'
import { ok, err } from 'neverthrow'
import type SessionService from 'teacher-ui-client/services/session'
import type Store from '@ember-data/store'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { sortBy } from 'lodash'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'

export default class StudentCsvMaker extends Service {
  @service fileDownload!: FileDownload

  @service session!: SessionService

  @service store!: Store

  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service subscriptionType!: SubscriptionTypeService

  downloadRolloverCsv = task({ drop: true }, async (filename: string) => {
    let students: Student[] = []
    try {
      students = await this.store.query<Student>('student', {
        // @ts-expect-error - include types not correct
        include: 'school-classes,school-classes.teachers',
      })

      const csvDataArray = this.rolloverCsvArray(students)
      this.fileDownload.downloadAsCsv(csvDataArray, {
        filename,
      })
      return ok({ studentCount: students.length })
    } catch (error: unknown) {
      return err({ error, studentCount: students.length })
    }
  })

  rolloverCsvArray(students: Student[]) {
    const headerRow = [
      'manage.rollover.steps.students.export.fields.studentId',
      'manage.rollover.steps.students.export.fields.firstName',
      'manage.rollover.steps.students.export.fields.lastName',
      'grade.label',
      'manage.rollover.steps.students.export.fields.teacherEmail',
      'manage.rollover.steps.students.export.fields.externalId',
      'manage.rollover.steps.students.export.fields.className',
    ].map((key) => this.intl.t(key))

    const data = sortBy([...students], ['gradePosition', 'lastName', 'firstName']).map((student) => {
      const subSchoolClasses = student.schoolClasses.filter(
        (schoolClass) => schoolClass.subscriptionType === this.subscriptionType.current,
      )
      const latestSchoolClass = subSchoolClasses.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())[0]
      const classTeacher = latestSchoolClass?.teachers[0]

      const className = latestSchoolClass?.name ?? ''
      const teacherEmail = classTeacher?.email ?? ''
      const studentGradePosition = student.gradePosition
      const studentGrade = this.gradeSets.findByPosition(studentGradePosition)
      const studentGradeName = studentGrade.name

      return [
        student.id,
        student.firstName,
        student.lastName,
        studentGradeName,
        teacherEmail,
        student.externalId,
        className,
      ]
    })
    return [headerRow, ...data]
  }

  /**
   * Turns a list of students into an array format which can be passed onto a csv generator for instance
   * @param {DS.RecordArray<StudentModel>} students
   * @returns {Array} - the result will be like: [['', 'klaus', 'dieter', 1],[...],[...]]
   */
  studentDataCsvArray(students: Student[]) {
    const hideAuthFields = this.session.currentSchool.usesExternalAuthentication

    let headerRow
    if (hideAuthFields) {
      headerRow = [
        this.intl.t('manage.firstName'),
        this.intl.t('manage.lastName'),
        this.intl.t('grade.label'),
        this.intl.t('manage.studentId'),
      ]
    } else {
      headerRow = [
        this.intl.t('manage.firstName'),
        this.intl.t('manage.lastName'),
        this.intl.t('manage.login'),
        this.intl.t('manage.password'),
        this.intl.t('grade.label'),
        this.intl.t('manage.studentId'),
      ]
    }

    const mappedStudents = sortBy([...students], ['gradePosition', 'lastName', 'firstName']).map((student) => {
      const grade = this.gradeSets.findByPosition(student.gradePosition)

      if (hideAuthFields) {
        return [student.firstName || '', student.lastName || '', grade.fullName || '', student.externalId || '']
      } else {
        return [
          student.firstName || '',
          student.lastName || '',
          student.login || '',
          student.plainPassword || '',
          grade.fullName || '',
          student.externalId || '',
        ]
      }
    })
    return [headerRow, ...mappedStudents]
  }
}

declare module '@ember/service' {
  interface Registry {
    'student-csv-maker': StudentCsvMaker
  }
}
